import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { X, ChevronLeft, ChevronRight, Play, Volume2, VolumeX, Trash2, Eye, ExternalLink, Heart, Flag, Plus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { getMediaUrl } from '../utils/mediaUrl';
import VerifiedBadge from './VerifiedBadge';
import AddStoryModal from './AddStoryModal';
import { useSectionsVisibility } from '../hooks/useSectionsVisibility';

// ============================================================================
// StoryTextOverlays — renders user-placed text overlays + @mentions on top of
// a story's image/video. Mentions become clickable links to the user's profile.
// ============================================================================
const StoryTextOverlays = ({ overlays, onMentionClick }) => {
  if (!Array.isArray(overlays) || overlays.length === 0) return null;
  const renderText = (text, mentions) => {
    if (!text) return null;
    const map = {};
    (mentions || []).forEach(m => { if (m.username) map[m.username.toLowerCase()] = m; });
    const parts = text.split(/(@[a-z0-9_]{3,20})/gi);
    return parts.map((p, i) => {
      if (p && p.startsWith('@')) {
        const key = p.slice(1).toLowerCase();
        const m = map[key];
        // Always clickable: prefer stored user_id, fallback to username lookup via /profile/u/<name>
        return (
          <span
            key={i}
            onClick={(e) => { e.stopPropagation(); onMentionClick?.(m?.user_id, m?.username || key); }}
            style={{ color: '#f0abfc', cursor: 'pointer', textDecoration: 'underline' }}
            data-testid={`story-view-mention-${key}`}
          >{p}</span>
        );
      }
      return <React.Fragment key={i}>{p}</React.Fragment>;
    });
  };
  return (
    <div className="absolute inset-0 pointer-events-none z-20" data-testid="story-overlay-layer">
      {overlays.map(o => o.type === 'sticker' ? (
        <div
          key={o.id}
          className="absolute pointer-events-none select-none"
          style={{
            left: `${((o.x == null ? 0.5 : o.x)) * 100}%`,
            top: `${((o.y == null ? 0.5 : o.y)) * 100}%`,
            transform: `translate(-50%, -50%) rotate(${o.rotation || 0}deg) scale(${o.scale || 1})`,
            fontSize: '64px',
            lineHeight: 1,
            filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.45))',
          }}
        >
          {o.emoji}
        </div>
      ) : (
        <div
          key={o.id}
          className="absolute pointer-events-auto px-2 py-1 select-none"
          style={{
            left: `${((o.x == null ? 0.5 : o.x)) * 100}%`,
            top: `${((o.y == null ? 0.5 : o.y)) * 100}%`,
            transform: `translate(-50%, -50%) rotate(${o.rotation || 0}deg) scale(${o.scale || 1})`,
            color: o.color || '#ffffff',
            fontSize: `${o.font_size || 28}px`,
            fontFamily: o.font_family === 'serif' ? 'Georgia, serif'
                      : o.font_family === 'mono' ? 'monospace'
                      : 'system-ui, sans-serif',
            fontWeight: 700,
            textShadow: (o.bg || 'none') === 'none' ? '0 1px 2px rgba(0,0,0,0.7), 0 0 2px rgba(0,0,0,0.5)' : 'none',
            background: o.bg === 'black' ? 'rgba(0,0,0,0.55)'
                      : o.bg === 'white' ? 'rgba(255,255,255,0.9)'
                      : o.bg === 'gradient' ? 'linear-gradient(135deg, #c026d3, #7c3aed)'
                      : 'transparent',
            borderRadius: (o.bg || 'none') === 'none' ? 0 : 8,
            whiteSpace: 'pre-wrap',
            lineHeight: 1.15,
            maxWidth: '85%',
            wordBreak: 'break-word',
            textAlign: 'center',
          }}
        >
          {renderText(o.text, o.mentions)}
        </div>
      ))}
    </div>
  );
};

// Portal component pour rendre le modal en dehors du DOM parent
const StoryViewerPortal = ({ children }) => {
  const [mounted, setMounted] = useState(false);
  
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);
  
  if (!mounted) return null;
  
  return ReactDOM.createPortal(
    children,
    document.body
  );
};

const StoryBar = ({ showAdd = true, testId }) => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { homeHidden } = useSectionsVisibility();
  const [storyGroups, setStoryGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showViewers, setShowViewers] = useState(false);
  const [viewers, setViewers] = useState([]);
  const [loadingViewers, setLoadingViewers] = useState(false);
  const [storyAd, setStoryAd] = useState(null);
  const [showingAd, setShowingAd] = useState(false);
  // Like / Report state for the currently displayed story
  const [iLiked, setILiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  // Avatar story audio state
  const [avatarAudioPlaying, setAvatarAudioPlaying] = useState(false);
  const [mouthOpen, setMouthOpen] = useState(0);
  const [showAddStoryModal, setShowAddStoryModal] = useState(false);
  const scrollRef = useRef(null);
  const timerRef = useRef(null);
  const videoRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const avatarAudioRef = useRef(null);
  const mouthIntervalRef = useRef(null);

  useEffect(() => {
    if (user) {
      loadStories();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadStories = async () => {
    try {
      const response = await axios.get('/api/stories/following');
      setStoryGroups(response.data || []);
    } catch (error) {
      console.error('Error loading stories:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetched lazily when a story is opened (avoids counting impressions on mount)
  const loadStoryAd = async () => {
    try {
      const [adRes, promoRes] = await Promise.allSettled([
        axios.get('/api/advertisements/random?zone=stories'),
        homeHidden ? axios.get('/api/promote-profile/feed-slot') : Promise.reject(new Error('skip')),
      ]);
      const candidates = [];
      const ad = adRes.status === 'fulfilled' ? adRes.value.data : null;
      // v50.10.9 — image, video AND audio ads can open a story
      if (ad && ad.id && ['image', 'video', 'audio'].includes(ad.ad_type)) candidates.push(ad);
      const promoted = promoRes.status === 'fulfilled' ? promoRes.value.data?.promoted_user : null;
      // v60.3 — Accueil masqué : les profils promus (flamme) passent dans les stories, tirés au sort avec les pubs
      if (promoted && promoted.id) candidates.push({ id: `promo-${promoted.id}`, ad_type: 'profile', promoted_user: promoted });
      if (candidates.length) {
        const pick = candidates[Math.floor(Math.random() * candidates.length)];
        setStoryAd(pick);
        return pick;
      }
    } catch (error) {
      console.error('Error loading story ad:', error);
    }
    setStoryAd(null);
    return null;
  };


  // Open someone else's story from outside the bar (e.g. when the user taps a
  // colored avatar in the Vocaux feed). Fetches their stories on demand even
  // if they're not in the user's followed-groups list.
  const openUserStory = async (targetUserId) => {
    try {
      const existing = storyGroups.find((g) => g.user?.id === targetUserId);
      if (existing) { openStoryViewer(existing); return; }
      const res = await axios.get(`/api/stories/user/${targetUserId}`);
      const stories = Array.isArray(res.data) ? res.data : (res.data?.stories || []);
      if (!Array.isArray(stories) || stories.length === 0) return;
      const u = res.data?.user || stories[0].user || { id: targetUserId };
      const group = { user: u, stories, viewed_count: 0, is_own: u.id === user?.id, has_unviewed: true };
      openStoryViewer(group);
    } catch (e) { /* silent — story may have expired */ }
  };

  // Watch for ?story_user= query param so a Vocaux avatar click can open a
  // specific user's story right inside the home Story bar.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const target = params.get('story_user');
    if (target) {
      openUserStory(target);
      params.delete('story_user');
      navigate(`${location.pathname}${params.toString() ? '?' + params.toString() : ''}`, { replace: true });
    }
  }, [location.search, storyGroups]); // eslint-disable-line

  const openStoryViewer = async (group) => {
    setSelectedGroup(group);
    const ad = await loadStoryAd();
    // Show ad first if available
    if (ad) {
      setShowingAd(true);
      setCurrentStoryIndex(-1); // -1 indicates showing ad
      trackAdView();
    } else {
      setCurrentStoryIndex(0);
      if (group.stories[0]) {
        markAsViewed(group.stories[0].id);
      }
    }
    setIsPlaying(true);
    setProgress(0);
    setIsMuted(false);
    // Empêcher le scroll du body
    document.body.style.overflow = 'hidden';
  };

  const trackAdView = async () => {
    if (storyAd && storyAd.ad_type !== 'profile') {
      try {
        await axios.post(`/api/advertisements/${storyAd.id}/view`);
      } catch (error) {
        console.error('Error tracking ad view:', error);
      }
    }
  };

  const trackAdClick = async () => {
    if (storyAd && storyAd.ad_type !== 'profile') {
      try {
        await axios.post(`/api/advertisements/${storyAd.id}/click`);
      } catch (error) {
        console.error('Error tracking ad click:', error);
      }
    }
  };

  const markAsViewed = async (storyId) => {
    try {
      await axios.post(`/api/stories/${storyId}/view`);
    } catch (error) {
      console.error('Error marking story as viewed:', error);
    }
  };

  const loadViewers = async (storyId) => {
    setLoadingViewers(true);
    try {
      const response = await axios.get(`/api/stories/${storyId}/viewers`);
      setViewers(response.data);
    } catch (error) {
      console.error('Error loading viewers:', error);
      toast.error('Erreur lors du chargement des vues');
    } finally {
      setLoadingViewers(false);
    }
  };

  const toggleViewers = () => {
    if (!showViewers && currentStory) {
      loadViewers(currentStory.id);
    }
    setShowViewers(!showViewers);
    // Pause story when showing viewers
    if (!showViewers) {
      setIsPlaying(false);
      clearInterval(progressIntervalRef.current);
      clearTimeout(timerRef.current);
    } else {
      setIsPlaying(true);
    }
  };

  const nextStory = () => {
    if (!selectedGroup) return;
    
    stopMedia();
    clearInterval(progressIntervalRef.current);
    clearTimeout(timerRef.current);
    setProgress(0);
    
    // If currently showing ad, move to first story
    if (showingAd) {
      setShowingAd(false);
      setCurrentStoryIndex(0);
      if (selectedGroup.stories[0]) {
        markAsViewed(selectedGroup.stories[0].id);
      }
      return;
    }
    
    if (currentStoryIndex < selectedGroup.stories.length - 1) {
      const nextIndex = currentStoryIndex + 1;
      setCurrentStoryIndex(nextIndex);
      markAsViewed(selectedGroup.stories[nextIndex].id);
    } else {
      const currentGroupIndex = storyGroups.findIndex(g => g.user.id === selectedGroup.user.id);
      if (currentGroupIndex !== -1 && currentGroupIndex < storyGroups.length - 1) {
        const nextGroup = storyGroups[currentGroupIndex + 1];
        setSelectedGroup(nextGroup);
        // Show ad again for new group if available
        if (storyAd) {
          setShowingAd(true);
          setCurrentStoryIndex(-1);
          trackAdView();
        } else {
          setCurrentStoryIndex(0);
          markAsViewed(nextGroup.stories[0].id);
        }
      } else {
        closeViewer();
      }
    }
  };

  const prevStory = () => {
    if (!selectedGroup) return;
    
    stopMedia();
    clearInterval(progressIntervalRef.current);
    clearTimeout(timerRef.current);
    setProgress(0);
    
    if (currentStoryIndex > 0) {
      setCurrentStoryIndex(currentStoryIndex - 1);
    } else {
      const currentGroupIndex = storyGroups.findIndex(g => g.user.id === selectedGroup.user.id);
      if (currentGroupIndex > 0) {
        const prevGroup = storyGroups[currentGroupIndex - 1];
        setSelectedGroup(prevGroup);
        setCurrentStoryIndex(prevGroup.stories.length - 1);
      }
    }
  };

  const stopMedia = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    if (avatarAudioRef.current) {
      try { avatarAudioRef.current.pause(); } catch (_) {}
    }
    stopMouthAnim();
  };

  // Fermer la pub pour voir directement la story (v50.10.9)
  const skipAd = (e) => {
    if (e) e.stopPropagation();
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    setShowingAd(false);
    setProgress(0);
    setCurrentStoryIndex(0);
    if (selectedGroup?.stories?.[0]) markAsViewed(selectedGroup.stories[0].id);
    setIsPlaying(true);
  };

  const closeViewer = () => {
    stopMedia();
    clearInterval(progressIntervalRef.current);
    clearTimeout(timerRef.current);
    setSelectedGroup(null);
    setCurrentStoryIndex(0);
    setIsPlaying(true);
    setProgress(0);
    // Réactiver le scroll du body
    document.body.style.overflow = '';
    loadStories();
  };

  const deleteStory = async (storyId) => {
    if (!window.confirm('Supprimer cette story ?')) return;
    
    try {
      await axios.delete(`/api/stories/${storyId}`);
      toast.success('Story supprimée');
      
      // Si c'était la dernière story du groupe, fermer le viewer
      if (selectedGroup.stories.length === 1) {
        closeViewer();
      } else {
        // Sinon, passer à la story suivante ou précédente
        const newStories = selectedGroup.stories.filter(s => s.id !== storyId);
        setSelectedGroup({ ...selectedGroup, stories: newStories });
        if (currentStoryIndex >= newStories.length) {
          setCurrentStoryIndex(newStories.length - 1);
        }
      }
    } catch (error) {
      console.error('Error deleting story:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleContentClick = () => {
    const currentStory = selectedGroup?.stories[currentStoryIndex];
    console.log('handleContentClick called', currentStory);
    
    if (!currentStory) {
      console.log('No current story');
      return;
    }
    
    // Fermer le viewer d'abord
    closeViewer();
    
    // Naviguer vers le contenu
    if (currentStory.linked_content_id && currentStory.linked_content_type === 'gim_toli') {
      console.log('Navigating to gim_toli:', currentStory.linked_content_id);
      navigate(`/feed?post=${currentStory.linked_content_id}`);
    } else if (currentStory.linked_content_id && currentStory.linked_content_type === 'podcast') {
      console.log('Navigating to podcast:', currentStory.linked_content_id);
      navigate(`/podcasts?id=${currentStory.linked_content_id}`);
    } else {
      // Si pas de linked_content_id, juste fermer et aller à l'accueil
      console.log('No linked content, going to home');
      navigate('/feed');
    }
  };

  useEffect(() => {
    if (selectedGroup && isPlaying) {
      // Handle ad display
      if (showingAd && storyAd) {
        const duration = (storyAd.ad_type === 'image' || storyAd.ad_type === 'profile') ? 5000 : 15000; // images/profils 5s, vidéo/audio 15s max
        const interval = 50;
        let elapsed = 0;
        
        progressIntervalRef.current = setInterval(() => {
          elapsed += interval;
          setProgress((elapsed / duration) * 100);
          
          if (elapsed >= duration) {
            clearInterval(progressIntervalRef.current);
            nextStory();
          }
        }, interval);
        
        return () => {
          clearInterval(progressIntervalRef.current);
          clearTimeout(timerRef.current);
        };
      }
      
      // Handle regular stories
      const currentStory = selectedGroup.stories[currentStoryIndex];
      if (!currentStory) return;
      
      const isVideo = currentStory?.content_type === 'video' || currentStory?.media_type === 'video';
      const isAvatarVoice = currentStory?.source === 'studio_avatar' && currentStory?.audio_url;
      
      // Pour les vidéos, on laisse la vidéo contrôler la progression (jusqu'à 30 secondes)
      // Pour les images et contenus partagés, on utilise un timer de 5 secondes
      // Pour les avatar-voice stories, on utilise la durée audio (fallback 30s)
      if (!isVideo) {
        const audioDur = Number(currentStory?.audio_duration || 0);
        const duration = isAvatarVoice
          ? Math.max(8000, Math.min(60000, (audioDur || 30) * 1000))
          : 5000; // 5 secondes
        const interval = 50;
        let elapsed = 0;
        
        progressIntervalRef.current = setInterval(() => {
          elapsed += interval;
          setProgress((elapsed / duration) * 100);
          
          if (elapsed >= duration) {
            clearInterval(progressIntervalRef.current);
            nextStory();
          }
        }, interval);
      }

      return () => {
        clearInterval(progressIntervalRef.current);
        clearTimeout(timerRef.current);
      };
    }
  }, [selectedGroup, currentStoryIndex, isPlaying, showingAd]);

  // Gérer la lecture vidéo
  useEffect(() => {
    if (videoRef.current && selectedGroup && isPlaying) {
      videoRef.current.muted = isMuted;
      videoRef.current.play().catch(console.error);
    }
  }, [isPlaying, selectedGroup, currentStoryIndex, isMuted]);

  // Sync like state on each story change
  useEffect(() => {
    const cs = selectedGroup?.stories?.[currentStoryIndex];
    if (!cs) return;
    setLikeCount(cs.likes_count ?? (Array.isArray(cs.likes) ? cs.likes.length : 0));
    setILiked(Array.isArray(cs.likes) && user?.id ? cs.likes.includes(user.id) : false);
  }, [selectedGroup, currentStoryIndex, user?.id]);

  // Avatar voice playback + lip-sync mouth animation (synced via setInterval)
  const startMouthAnim = () => {
    if (mouthIntervalRef.current) return;
    let t = 0;
    mouthIntervalRef.current = setInterval(() => {
      t += 0.18;
      const base = (Math.sin(t * 4) + 1) / 2;
      const jitter = (Math.random() - 0.5) * 0.25;
      const next = Math.max(0, Math.min(1, base * 0.65 + jitter + 0.15));
      setMouthOpen(next);
    }, 80);
  };
  const stopMouthAnim = () => {
    if (mouthIntervalRef.current) {
      clearInterval(mouthIntervalRef.current);
      mouthIntervalRef.current = null;
    }
    setMouthOpen(0);
  };

  // Auto-play avatar audio whenever story changes & is an avatar story
  useEffect(() => {
    const cs = selectedGroup?.stories?.[currentStoryIndex];
    const isAvatarVoice = cs && cs.source === 'studio_avatar' && cs.audio_url;
    if (!isAvatarVoice) {
      // Pause any leftover audio when switching to non-avatar stories
      try { avatarAudioRef.current?.pause(); } catch (_) {}
      stopMouthAnim();
      return;
    }
    // Defer one tick so the <audio> element is mounted
    const tid = setTimeout(() => {
      const a = avatarAudioRef.current;
      if (!a) return;
      try { a.currentTime = 0; } catch (_) {}
      a.play().catch(() => {/* autoplay blocked — user can tap big play overlay */});
    }, 100);
    return () => { clearTimeout(tid); stopMouthAnim(); };
    // eslint-disable-next-line
  }, [selectedGroup, currentStoryIndex]);

  const toggleAvatarAudio = (e) => {
    if (e) e.stopPropagation();
    const a = avatarAudioRef.current;
    if (!a) return;
    if (avatarAudioPlaying) { try { a.pause(); } catch (_) {} }
    else { a.play().catch(() => {}); }
  };

  const toggleLikeCurrent = async (e) => {
    if (e) e.stopPropagation();
    const cs = selectedGroup?.stories?.[currentStoryIndex];
    if (!cs) return;
    try {
      const res = await axios.post(`/api/stories/${cs.id}/like`);
      setILiked(res.data.liked);
      setLikeCount(res.data.likes_count);
    } catch (_) {
      toast.error('Action impossible');
    }
  };

  const reportCurrent = async (e) => {
    if (e) e.stopPropagation();
    const cs = selectedGroup?.stories?.[currentStoryIndex];
    if (!cs) return;
    const reason = window.prompt("Pourquoi signaler cette story ?");
    if (!reason || !reason.trim()) return;
    try {
      await axios.post(`/api/stories/${cs.id}/report`, { reason: reason.trim() });
      toast.success('Signalement envoyé aux modérateurs');
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Signalement impossible");
    }
  };

  const handleVideoTimeUpdate = () => {
    if (videoRef.current) {
      const percentage = (videoRef.current.currentTime / videoRef.current.duration) * 100;
      setProgress(percentage);
    }
  };

  const handleMediaEnded = () => {
    nextStory();
  };

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -150, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 150, behavior: 'smooth' });
    }
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
    if (videoRef.current) {
      isPlaying ? videoRef.current.pause() : videoRef.current.play();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center space-x-3 px-4 py-2 overflow-x-auto">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="flex-shrink-0 w-12 h-12 rounded-full bg-white/10 animate-pulse" />
        ))}
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const currentStory = selectedGroup?.stories[currentStoryIndex];
  // Priorité: content_url > media_url > thumbnail_url - avec URL complète
  const storyUrl = getMediaUrl(currentStory?.content_url || currentStory?.media_url || currentStory?.thumbnail_url || '');
  const isVideoStory = currentStory?.content_type === 'video' || 
                       currentStory?.media_type === 'video' ||
                       storyUrl.toLowerCase().endsWith('.mp4') || 
                       storyUrl.toLowerCase().endsWith('.webm') ||
                       storyUrl.toLowerCase().endsWith('.mov');
  // Un contenu lié est un Gim Toli ou Podcast partagé (pas une image/vidéo uploadée directement)
  const hasLinkedContent = currentStory?.linked_content_type === 'gim_toli' || currentStory?.linked_content_type === 'podcast';
  // Avatar voice story (image of avatar + recorded user voice)
  const isAvatarStory = currentStory?.source === 'studio_avatar' && !!currentStory?.audio_url;
  const isOwnStory = !!selectedGroup?.is_own;

  return (
    <>
      {/* Story Bar */}
      <div className="relative flex items-center" data-testid={testId}>
        {storyGroups.length > 4 && (
          <button onClick={scrollLeft} className="flex-shrink-0 p-1 hover:bg-white/10 rounded-full transition-colors">
            <ChevronLeft className="w-4 h-4 text-gray-400" />
          </button>
        )}

        <div
          ref={scrollRef}
          className="flex items-center space-x-3 px-2 overflow-x-auto scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {/* Add Story — always visible at the start of the bar. Moved here from
              the Profile page so the call-to-action lives where stories are
              actually consumed, and stays accessible even before any story
              exists in the user's feed. */}
          {showAdd && (
          <button
            onClick={() => setShowAddStoryModal(true)}
            className="flex-shrink-0 flex flex-col items-center group"
            data-testid="add-story-btn"
            title="Ajouter une story"
          >
            <div className="relative">
              <div className="w-12 h-12 rounded-full p-[2px] bg-gray-700">
                <div className="w-full h-full rounded-full bg-black flex items-center justify-center">
                  {user.profile_image_url || user.avatar_url || user.picture ? (
                    <img
                      src={getMediaUrl(user.profile_image_url || user.avatar_url || user.picture)}
                      alt="Vous"
                      className="w-full h-full rounded-full object-cover opacity-90"
                    />
                  ) : (
                    <div className="w-full h-full rounded-full bg-gradient-to-br from-magenta-500 to-violet-600 flex items-center justify-center">
                      <span className="text-white font-bold text-sm">{user.name?.[0]?.toUpperCase() || '+'}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-gradient-to-br from-violet-500 to-magenta-600 border-2 border-black flex items-center justify-center shadow-lg">
                <Plus className="w-3 h-3 text-white" strokeWidth={3} />
              </div>
            </div>
            <span className="text-[10px] text-gray-400 mt-1 truncate max-w-[60px] group-hover:text-white transition-colors">Ajouter</span>
          </button>
          )}

          {storyGroups.map((group) => (
            <button
              key={group.user.id}
              onClick={() => openStoryViewer(group)}
              className="flex-shrink-0 flex flex-col items-center group"
            >
              <div className="relative">
                <div className={`w-12 h-12 rounded-full p-[2px] ${
                  group.has_unviewed 
                    ? 'bg-gradient-to-br from-magenta-500 via-pink-500 to-violet-600' 
                    : 'bg-gray-600'
                }`}>
                  <div className="w-full h-full rounded-full bg-black p-[2px]">
                    {/* Photo de profil de l'utilisateur */}
                    {group.user.profile_image_url || group.user.avatar_url || group.user.picture ? (
                      <img
                        src={getMediaUrl(group.user.profile_image_url || group.user.avatar_url || group.user.picture)}
                        alt={group.user.name}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full rounded-full bg-gradient-to-br from-magenta-500 to-violet-600 flex items-center justify-center">
                        <span className="text-white font-bold text-sm">
                          {group.user.name?.[0]?.toUpperCase() || '?'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                {group.is_own && (
                  <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-magenta-500 rounded-full flex items-center justify-center border-2 border-black text-[8px]">
                    👤
                  </div>
                )}
                {!group.is_own && group.user.is_verified && (
                  <div className="absolute -bottom-0.5 -right-0.5">
                    <VerifiedBadge size="xs" />
                  </div>
                )}
              </div>
              <span className="text-[10px] text-gray-400 mt-1 max-w-[50px] truncate group-hover:text-white transition-colors">
                {group.is_own ? 'Moi' : group.user.name?.split(' ')[0]}
              </span>
            </button>
          ))}
        </div>

        {storyGroups.length > 4 && (
          <button onClick={scrollRight} className="flex-shrink-0 p-1 hover:bg-white/10 rounded-full transition-colors">
            <ChevronRight className="w-4 h-4 text-gray-400" />
          </button>
        )}
      </div>

      {/* Story Viewer Modal - Rendu via Portal pour être en plein écran */}
      {selectedGroup && (
        <StoryViewerPortal>
          <div 
            className="fixed inset-0 bg-black"
            style={{ zIndex: 99999 }}
          >
            {/* Progress bars */}
            <div className="absolute top-4 left-4 right-4 flex space-x-1" style={{ zIndex: 100001 }}>
              {/* Ad progress bar if showing ad */}
              {showingAd && storyAd && (
                <div className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-violet-500 transition-all duration-100"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              )}
              {/* Regular story progress bars */}
              {!showingAd && selectedGroup.stories.map((_, index) => (
                <div key={index} className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-white transition-all duration-100"
                    style={{
                      width: index < currentStoryIndex ? '100%' :
                             index === currentStoryIndex ? `${progress}%` : '0%'
                    }}
                  />
                </div>
              ))}
            </div>

            {/* Header */}
            <div className="absolute top-10 left-4 right-4 flex items-center justify-between" style={{ zIndex: 100001 }}>
              {showingAd && storyAd ? (
                /* Ad Header */
                <div className="flex items-center space-x-3">
                  <div className="px-3 py-1 bg-violet-500/80 backdrop-blur-sm text-white text-xs font-semibold rounded-full">
                    Sponsorisé
                  </div>
                  <p className="text-white font-semibold">{storyAd.title || 'Publicité'}</p>
                </div>
              ) : (
                /* Normal story header */
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-magenta-500 to-violet-600 flex items-center justify-center overflow-hidden">
                    {selectedGroup.user.profile_image_url || selectedGroup.user.avatar_url || selectedGroup.user.picture ? (
                      <img
                        src={getMediaUrl(selectedGroup.user.profile_image_url || selectedGroup.user.avatar_url || selectedGroup.user.picture)}
                        alt={selectedGroup.user.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-white font-bold">
                        {selectedGroup.user.name?.[0]?.toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div>
                    <p className="text-white font-semibold flex items-center gap-1">
                      {selectedGroup.user.name}
                      {selectedGroup.user.is_verified && <VerifiedBadge size="sm" />}
                    </p>
                    <p className="text-gray-400 text-xs">
                      {currentStory && new Date(currentStory.created_at).toLocaleTimeString('fr-FR', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              )}
              
              <div className="flex items-center space-x-2">
                {/* Compteur de vues - cliquable pour voir la liste */}
                {selectedGroup.is_own && currentStory && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleViewers();
                    }}
                    className="flex items-center space-x-1 px-3 py-1.5 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
                    title="Voir qui a vu cette story"
                  >
                    <Eye className="w-4 h-4 text-white" />
                    <span className="text-white text-sm font-medium">
                      {currentStory.views_count || 0}
                    </span>
                  </button>
                )}
                
                {/* Like — always visible (owner can also like own story) */}
                {!showingAd && currentStory && (
                  <button
                    onClick={toggleLikeCurrent}
                    className="flex items-center space-x-1 px-3 py-1.5 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
                    title="J'aime"
                    data-testid="story-like-btn"
                  >
                    <Heart className={`w-4 h-4 ${iLiked ? 'text-rose-400 fill-rose-400' : 'text-white'}`} />
                    <span className="text-white text-sm font-medium">{likeCount}</span>
                  </button>
                )}

                {/* Report — hidden for own stories */}
                {!showingAd && currentStory && !isOwnStory && (
                  <button
                    onClick={reportCurrent}
                    className="p-2 bg-black/50 hover:bg-amber-500/40 rounded-full transition-colors"
                    title="Signaler"
                    data-testid="story-report-btn"
                  >
                    <Flag className="w-5 h-5 text-white" />
                  </button>
                )}

                {/* Bouton corbeille - uniquement pour ses propres stories */}
                {selectedGroup.is_own && currentStory && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteStory(currentStory.id);
                    }}
                    className="p-2 bg-red-500/70 hover:bg-red-500 rounded-full transition-colors"
                    title="Supprimer cette story"
                  >
                    <Trash2 className="w-5 h-5 text-white" />
                  </button>
                )}
                
                {(isVideoStory || hasLinkedContent) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsMuted(!isMuted);
                      if (videoRef.current) videoRef.current.muted = !isMuted;
                    }}
                    className="p-2 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
                  >
                    {isMuted ? <VolumeX className="w-6 h-6 text-white" /> : <Volume2 className="w-6 h-6 text-white" />}
                  </button>
                )}
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    closeViewer();
                  }}
                  className="p-2 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
                >
                  <X className="w-6 h-6 text-white" />
                </button>
              </div>
            </div>

            {/* Contenu principal - PLEIN ÉCRAN */}
            <div className="absolute inset-0 flex items-center justify-center" style={{ zIndex: 100000 }}>
              {/* Ad Content */}
              {showingAd && storyAd && (
                <div className="w-full h-full flex flex-col items-center justify-center relative" data-testid="story-ad">
                  <button
                    onClick={skipAd}
                    className="absolute top-20 right-4 px-3 py-1.5 rounded-full bg-black/60 border border-white/30 text-white text-xs font-semibold flex items-center gap-1 hover:bg-black/80"
                    style={{ zIndex: 100004 }}
                    data-testid="story-ad-skip"
                  >
                    <X className="w-3.5 h-3.5" /> {storyAd.ad_type === 'profile' ? ((language === 'fr' || language === 'ht') ? 'Passer' : 'Skip') : ((language === 'fr' || language === 'ht') ? 'Fermer la pub' : 'Skip ad')}
                  </button>
                  {storyAd.ad_type === 'video' && (
                    <video
                      src={getMediaUrl(storyAd.content_url)}
                      className="w-full h-full object-contain"
                      autoPlay
                      playsInline
                      muted={isMuted}
                      onEnded={skipAd}
                      data-testid="story-ad-video"
                    />
                  )}
                  {storyAd.ad_type === 'audio' && (
                    <div className="w-full h-full flex flex-col items-center justify-center gap-6 bg-gradient-to-br from-violet-900 via-black to-fuchsia-900" data-testid="story-ad-audio">
                      <div className="w-40 h-40 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center shadow-2xl animate-pulse">
                        <Volume2 className="w-16 h-16 text-white" />
                      </div>
                      <p className="text-white font-bold text-xl text-center px-6">{storyAd.title || 'Publicité'}</p>
                      <audio src={getMediaUrl(storyAd.content_url)} autoPlay onEnded={skipAd} muted={isMuted} />
                    </div>
                  )}
                  {storyAd.ad_type === 'image' && (
                  <img
                    src={getMediaUrl(storyAd.content_url)}
                    alt="Publicité"
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23333" width="100" height="100"/><text fill="%23fff" x="50%" y="50%" text-anchor="middle" dy=".3em">Pub</text></svg>';
                    }}
                  />
                  )}
                  {storyAd.ad_type === 'profile' && storyAd.promoted_user && (
                    <div className="w-full h-full relative bg-black" data-testid="story-promoted-profile">
                      {storyAd.promoted_user.profile_image_url ? (
                        <img src={getMediaUrl(storyAd.promoted_user.profile_image_url)} alt={storyAd.promoted_user.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-orange-600 via-fuchsia-700 to-violet-900">
                          <span className="text-8xl font-bold text-white">{(storyAd.promoted_user.name || '?')[0]?.toUpperCase()}</span>
                        </div>
                      )}
                      <div className="absolute inset-x-0 bottom-0 pt-32 pb-36 px-6 bg-gradient-to-t from-black via-black/80 to-transparent">
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-orange-500/30 text-orange-200 border border-orange-400/40 mb-2">🔥 {(language === 'fr' || language === 'ht') ? 'Profil à découvrir' : 'Featured profile'}</span>
                        <h3 className="text-2xl font-bold text-white flex items-center gap-2">{storyAd.promoted_user.name}{storyAd.promoted_user.is_verified && <VerifiedBadge size="sm" />}</h3>
                        {storyAd.promoted_user.username && <p className="text-sm text-gray-300">@{storyAd.promoted_user.username}</p>}
                        {(storyAd.promoted_user.bio || storyAd.promoted_user.description) && <p className="text-sm text-gray-200 mt-2 line-clamp-3">{storyAd.promoted_user.bio || storyAd.promoted_user.description}</p>}
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); closeViewer(); navigate(`/profile/${storyAd.promoted_user.id}`); }}
                        className="absolute bottom-20 left-1/2 -translate-x-1/2 px-6 py-3 bg-gradient-to-r from-orange-500 to-fuchsia-600 text-white font-semibold rounded-full shadow-xl flex items-center gap-2"
                        style={{ zIndex: 100003 }}
                        data-testid="story-promoted-profile-cta"
                      >
                        {(language === 'fr' || language === 'ht') ? 'Voir le profil' : 'View profile'} <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  {/* CTA Button for Ad */}
                  {storyAd.external_link && (
                    <a
                      href={storyAd.external_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        e.stopPropagation();
                        trackAdClick();
                      }}
                      className="absolute bottom-20 left-1/2 transform -translate-x-1/2 px-6 py-3 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-semibold rounded-full transition-all flex items-center space-x-2 shadow-xl"
                      style={{ zIndex: 100003 }}
                    >
                      <span>En savoir plus</span>
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
              )}
              
              {/* Normal Story Content */}
              {!showingAd && currentStory && (
                <>
                  {isAvatarStory ? (
                    <div className="w-full h-full flex flex-col items-center justify-center px-4 bg-gradient-to-br from-violet-950 via-black to-fuchsia-950">
                      <button
                        type="button"
                        onClick={toggleAvatarAudio}
                        className="relative rounded-3xl overflow-hidden shadow-2xl focus:outline-none"
                        style={{
                          width: 'min(78vw, 360px)',
                          height: 'min(78vw, 360px)',
                          transform: `scale(${1 + mouthOpen * 0.07})`,
                          boxShadow: `0 0 ${24 + mouthOpen * 60}px ${mouthOpen * 12}px rgba(217, 70, 239, ${0.3 + mouthOpen * 0.55})`,
                          transition: 'transform 60ms linear, box-shadow 60ms linear',
                        }}
                        data-testid="story-avatar-tap"
                        aria-label={avatarAudioPlaying ? 'Pause voix' : 'Lire la voix'}
                      >
                        {currentStory.image_url && (
                          <img
                            src={getMediaUrl(currentStory.image_url)}
                            alt="Avatar"
                            className="w-full h-full object-cover pointer-events-none"
                            data-testid="story-avatar-image"
                          />
                        )}
                        {avatarAudioPlaying && (
                          <div
                            className="absolute left-1/2 -translate-x-1/2 rounded-[50%] bg-black/55 border border-black/40 pointer-events-none"
                            style={{
                              bottom: '22%',
                              width: `${36 + mouthOpen * 22}px`,
                              height: `${4 + mouthOpen * 32}px`,
                              transition: 'width 60ms linear, height 60ms linear',
                              boxShadow: 'inset 0 -2px 4px rgba(255,255,255,0.15)',
                            }}
                          />
                        )}
                        {!avatarAudioPlaying && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none" data-testid="story-avatar-play-overlay">
                            <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-2xl">
                              <Play className="w-7 h-7 ml-1" fill="rgb(217,70,239)" stroke="none" />
                            </div>
                          </div>
                        )}
                      </button>
                      {currentStory.caption && (
                        <p className="mt-4 text-center text-white/90 text-sm bg-black/40 backdrop-blur-sm px-4 py-2 rounded-full max-w-[80vw]">
                          {currentStory.caption}
                        </p>
                      )}
                      <audio
                        ref={avatarAudioRef}
                        src={getMediaUrl(currentStory.audio_url)}
                        onPlay={() => { setAvatarAudioPlaying(true); startMouthAnim(); }}
                        onPause={() => { setAvatarAudioPlaying(false); stopMouthAnim(); }}
                        onEnded={() => { setAvatarAudioPlaying(false); stopMouthAnim(); }}
                        preload="auto"
                        playsInline
                        controls
                        className="mt-4 w-[min(90vw,340px)]"
                        data-testid="story-avatar-audio"
                      />
                    </div>
                  ) : isVideoStory ? (
                    <video
                      ref={videoRef}
                      src={storyUrl}
                      className="w-full h-full object-contain"
                      autoPlay
                      playsInline
                      muted={isMuted}
                      onTimeUpdate={handleVideoTimeUpdate}
                      onEnded={handleMediaEnded}
                    />
                  ) : hasLinkedContent ? (
                    <div 
                      className="w-full h-full flex flex-col items-center justify-center cursor-pointer relative"
                      style={{ zIndex: 100002 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log('Content clicked!');
                        handleContentClick();
                      }}
                    >
                      {/* Cover ou icône */}
                      {(currentStory.thumbnail_url || currentStory.media_url || currentStory.cover_image_url) ? (
                        <img
                          src={getMediaUrl(currentStory.thumbnail_url || currentStory.media_url || currentStory.cover_image_url)}
                          alt="Cover"
                          className="w-48 h-48 sm:w-64 sm:h-64 rounded-2xl object-cover shadow-2xl mb-4"
                        />
                      ) : (
                        <div className="w-48 h-48 sm:w-64 sm:h-64 rounded-2xl bg-gradient-to-br from-magenta-500 to-violet-600 flex items-center justify-center mb-4 shadow-2xl">
                          <span className="text-white text-6xl sm:text-8xl">🎵</span>
                        </div>
                      )}
                      
                      {/* Bouton Play et infos */}
                      <div className="flex flex-col items-center space-y-3">
                        <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-magenta-500 to-violet-600 rounded-full flex items-center justify-center hover:scale-110 transition-transform shadow-xl">
                          <Play className="w-8 h-8 sm:w-10 sm:h-10 text-white ml-1" fill="white" />
                        </div>
                        <p className="text-white font-semibold text-base sm:text-lg">
                          {currentStory.linked_content_type === 'gim_toli' ? 'Gim Toli' : 'Podcast'}
                        </p>
                        
                        {/* Titre du contenu */}
                        {currentStory.text_overlay && (
                          <p className="text-gray-300 text-sm sm:text-base text-center px-4 max-w-xs">
                            {currentStory.text_overlay}
                          </p>
                        )}
                      </div>
                    </div>
                  ) : storyUrl ? (
                    <img
                      src={storyUrl}
                      alt="Story"
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        console.error('Image load error:', storyUrl);
                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23333" width="100" height="100"/><text fill="%23fff" x="50%" y="50%" text-anchor="middle" dy=".3em">Image non disponible</text></svg>';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-900">
                      <p className="text-gray-400">Contenu non disponible</p>
                    </div>
                  )}

                  {/* User-placed text overlays (image/video stories) */}
                  {Array.isArray(currentStory?.overlays) && currentStory.overlays.length > 0 && (
                    <StoryTextOverlays
                      overlays={currentStory.overlays}
                      onMentionClick={(uid, username) => {
                        closeViewer();
                        navigate(uid ? `/profile/${uid}` : `/profile/u/${username}`);
                      }}
                    />
                  )}
                  
                  {!isPlaying && !hasLinkedContent && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); togglePlayPause(); }}
                      className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer"
                      data-testid="story-resume-btn"
                      aria-label="Lecture"
                    >
                      <div className="p-6 bg-black/50 rounded-full hover:bg-black/70 transition-colors">
                        <Play className="w-16 h-16 text-white" fill="white" />
                      </div>
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Navigation buttons — invisibles mais actifs (tap left/right) */}
            <button
              onClick={(e) => { e.stopPropagation(); prevStory(); }}
              className="absolute left-0 top-0 bottom-0 w-1/3 opacity-0"
              style={{ zIndex: 100003 }}
              aria-label="Précédent"
            />

            <button
              onClick={(e) => { e.stopPropagation(); nextStory(); }}
              className="absolute right-0 top-0 bottom-0 w-1/3 opacity-0"
              style={{ zIndex: 100003 }}
              aria-label="Suivant"
            />

            {/* Touch zones - uniquement sur les bords, pas au centre */}
            <div 
              className="absolute left-0 top-20 bottom-0 w-16"
              style={{ zIndex: 100001 }}
              onClick={(e) => { e.stopPropagation(); prevStory(); }}
            />
            <div 
              className="absolute right-0 top-20 bottom-0 w-16"
              style={{ zIndex: 100001 }}
              onClick={(e) => { e.stopPropagation(); nextStory(); }}
            />
            
            {/* Viewers Panel */}
            {showViewers && selectedGroup.is_own && (
              <div 
                className="absolute bottom-0 left-0 right-0 bg-black/95 backdrop-blur-md rounded-t-3xl max-h-[60%] overflow-y-auto"
                style={{ zIndex: 100004 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="sticky top-0 bg-black/95 backdrop-blur-md p-4 border-b border-white/10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Eye className="w-5 h-5 text-white" />
                      <h3 className="font-semibold text-white">
                        {viewers.length} vue{viewers.length > 1 ? 's' : ''}
                      </h3>
                    </div>
                    <button
                      onClick={() => setShowViewers(false)}
                      className="p-2 hover:bg-white/10 rounded-full transition-colors"
                    >
                      <X className="w-5 h-5 text-white" />
                    </button>
                  </div>
                </div>
                
                <div className="p-4">
                  {loadingViewers ? (
                    <div className="text-center text-gray-400 py-4">Chargement...</div>
                  ) : viewers.length === 0 ? (
                    <div className="text-center text-gray-400 py-4">
                      Personne n'a encore vu cette story
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {viewers.map(viewer => (
                        <div
                          key={viewer.id}
                          className="flex items-center space-x-3 p-2 hover:bg-white/5 rounded-xl transition-colors cursor-pointer"
                          onClick={() => {
                            closeViewer();
                            navigate(`/profile/${viewer.id}`);
                          }}
                        >
                          <div className="w-10 h-10 bg-gradient-to-br from-magenta-500 to-violet-600 rounded-full flex items-center justify-center overflow-hidden">
                            {viewer.profile_image_url ? (
                              <img
                                src={getMediaUrl(viewer.profile_image_url)}
                                alt={viewer.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="text-sm font-bold text-white">
                                {viewer.name?.charAt(0).toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-white">{viewer.name}</p>
                            {viewer.username && (
                              <p className="text-sm text-gray-400">@{viewer.username}</p>
                            )}
                            {viewer.viewed_at && (
                              <p className="text-xs text-gray-500 mt-0.5">
                                {new Date(viewer.viewed_at).toLocaleString(undefined, {
                                  day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                                })}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </StoryViewerPortal>
      )}
      {showAddStoryModal && (
        <AddStoryModal
          onClose={() => setShowAddStoryModal(false)}
          onSuccess={() => {
            setShowAddStoryModal(false);
            loadStories();
          }}
        />
      )}
    </>
  );
};

export default StoryBar;
